import { Component, ElementRef, type Signal, afterNextRender, computed, inject, input, output, signal } from '@angular/core';

import { BaseClass } from '@23degrees/shared/classes/base.class';

import type { Content } from '@23degrees/shared-code/domain/content/entities/valueobjects/Content';
import type { LayoutChild } from '@23degrees/shared-code/domain/content/entities/valueobjects/Layout';
import { IContentSelectorOptions, ISmartConfig } from '../..';

import { BackgroundBucketPipe, ResponsiveStylePipe } from '@23degrees/shared/pipes';
import { v4 as uuid } from 'uuid';

@Component({
  selector: 'layout-element',
  templateUrl: './layout-element.component.html',
  styleUrls: ['./layout-element.component.scss'],
  standalone: true,
  host: {
    '[class.flex]': 'true',
    '[class.hide-on-export]': 'childOptions().hideOnExport',
    '[style]': 'composeStyle()',
  },
  providers: [],
})
export class LayoutElementComponent extends BaseClass {
  private layoutElementRef = inject(ElementRef<HTMLElement>);
  private backgroundBucket = inject(BackgroundBucketPipe);

  public responsiveStylePipe = inject(ResponsiveStylePipe);

  public child = input.required<LayoutChild>();
  public config = input.required<ISmartConfig>();
  public content = input.required<Content>();
  public options = input.required<IContentSelectorOptions>();

  public context = input<{ ready: Signal<boolean>; [key: string]: unknown }>({ ready: signal(false) });

  public renderEvent = output<{ id?: string; type?: string; status?: string; [key: string]: any }>();

  public childOptions = computed(() => this.child().options);

  public contentUpdateHash = computed<string>(() => {
    const content = this.content();
    return uuid();
  });

  constructor() {
    super();

    afterNextRender(() => {
      this.emitRenderStatus('afterNextRender');
    });
  }

  ngOnInit() {
    this.emitRenderStatus('ngOnInit');
  }

  ngAfterViewInit() {
    this.emitRenderStatus('ngAfterViewInit');
  }

  public emitRenderStatus = (status = 'ready') => {
    this.renderEvent.emit({ id: this.child().id, type: this.child().type, status: status });
  };

  private getStyleInPx = (style: any, which: string): number => {
    const { offsetWidth, offsetHeight } = this.layoutElementRef.nativeElement;
    const relative = which === 'left' || which === 'right' ? offsetWidth : offsetHeight;
    let size = style[which];
    if (style[`${which}Unit`] === 'em') size *= 16;
    if (style[`${which}Unit`] === '%') size *= relative / 100;
    return size;
  };

  public composeStyle = computed(() => {
    const content = this.content();
    const child = this.child();

    const style = { ...child.style } as unknown as Partial<CSSStyleDeclaration>;

    if (style.width) {
      if (!Number.isNaN(style.width)) {
        style.width = `${style.width}%`;
      }
    }

    if (['search', 'map-legend'].includes(child.type)) {
      const { margin, padding, boxShadow, borderRadius } = child.options;
      style.margin = this.responsiveStylePipe.transform(margin, 'spacing');
      style.padding = this.responsiveStylePipe.transform(padding, 'spacing');
      style.boxShadow = this.responsiveStylePipe.transform(boxShadow, 'shadow');
      style.borderRadius = `${borderRadius}px`;
    }

    if (child.type === 'container') {
      style.margin = this.responsiveStylePipe.transform(child.options.margin, 'spacing');
      style.padding = this.responsiveStylePipe.transform(child.options.padding, 'spacing');

      style.backgroundColor = child.style.background.backgroundColor;
      style.background = this.backgroundBucket.transform(child.style.background.backgroundImage);

      if (child.style.position === 'absolute') {
        const subMargin = this.getStyleInPx(child.options.margin, 'left') + this.getStyleInPx(child.options.margin, 'right');
        const subPadding = this.getStyleInPx(child.options.padding, 'left') + this.getStyleInPx(child.options.padding, 'right');
        style.width = `calc(100% - ${subMargin + subPadding}px)`;
        style.pointerEvents = 'none';
      }
    } else {
      const pointerTypes = [
        'button',
        'visual',
        'menu',
        'filter',
        'legend',
        'source',
        'title',
        'subTitle',
        'description',
        'map-attributions',
        'filter-annotation',
        'navigation',
      ];

      style.pointerEvents = pointerTypes.includes(child.type) ? 'all' : 'none';
    }

    return style;
  });
}
